import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getMemberSession,
  loginWithOtp,
  logoutMember,
  type MemberSession,
  type OtpChannel,
} from "../services/member-auth.service";
import { clearWallet } from "../services/wallet.service";

type MemberAuthContextValue = {
  session: MemberSession | null;
  isLoading: boolean;
  login: (
    contact: string,
    channel: OtpChannel,
    requestId: string,
    code: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
};

const MemberAuthContext = createContext<MemberAuthContextValue | undefined>(
  undefined,
);

export function MemberAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<MemberSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setSession(getMemberSession());
    setIsLoading(false);
  }, []);

  const login = useCallback(
    async (contact: string, channel: OtpChannel, requestId: string, code: string) => {
      const next = await loginWithOtp(contact, channel, requestId, code);
      setSession(next);
    },
    [],
  );

  const logout = useCallback(async () => {
    await logoutMember();
    clearWallet();
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({ session, isLoading, login, logout }),
    [session, isLoading, login, logout],
  );

  return (
    <MemberAuthContext.Provider value={value}>
      {children}
    </MemberAuthContext.Provider>
  );
}

export function useMemberAuth() {
  const ctx = useContext(MemberAuthContext);
  if (!ctx) {
    throw new Error("useMemberAuth must be used within MemberAuthProvider");
  }
  return ctx;
}
